/**
 * Career Matching Quiz Page
 * Helps students discover careers that match their interests and preferences
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { calculateMatches } from '../../api/career-api';
import { Header } from '../../../../shared/components/Header';
import { Footer } from '../../../../shared/components/Footer';

interface MatchResult {
  domain: {
    id: number;
    name: string;
    slug: string;
    icon_emoji: string;
    short_description: string;
    avg_salary_usd: number;
    job_growth_percent: string;
    specialization_count: number;
  };
  match_score: number;
  matched_reasons: string[];
}

const CareerMatchPage: React.FC = () => {
  console.log('✨✨✨ CareerMatchPage component loaded!');

  const [step, setStep] = useState(1);
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // User selections
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [workStyle, setWorkStyle] = useState('');
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [learningStyle, setLearningStyle] = useState('');

  const interestOptions = [
    { value: 'problem-solving', label: 'Problem-solving', emoji: '🧩' },
    { value: 'technology', label: 'Technology & Innovation', emoji: '💻' },
    { value: 'helping_people', label: 'Helping People', emoji: '🤝' },
    { value: 'creativity', label: 'Creativity & Arts', emoji: '🎨' },
    { value: 'science', label: 'Science & Research', emoji: '🔬' },
    { value: 'leadership', label: 'Leadership & Strategy', emoji: '👔' },
    { value: 'writing', label: 'Writing & Communication', emoji: '✍️' },
    { value: 'building_things', label: 'Building & Creating', emoji: '🔧' },
    { value: 'understanding_people', label: 'Understanding People', emoji: '🧠' },
    { value: 'visual_arts', label: 'Visual Arts & Design', emoji: '🖼️' },
    { value: 'advocacy', label: 'Advocacy & Justice', emoji: '⚖️' },
    { value: 'storytelling', label: 'Storytelling & Media', emoji: '📺' }
  ];

  const workStyleOptions = [
    { value: 'analytical', label: 'Analytical & Data-Driven', description: 'I enjoy analyzing data and solving logical problems' },
    { value: 'creative', label: 'Creative & Artistic', description: 'I thrive on creative expression and innovative thinking' },
    { value: 'collaborative', label: 'Collaborative & Team-Oriented', description: 'I love working with teams and building relationships' },
    { value: 'hands-on', label: 'Hands-On & Practical', description: 'I prefer working with my hands and seeing tangible results' }
  ];

  const priorityOptions = [
    { value: 'high_salary', label: 'High Earning Potential', emoji: '💰' },
    { value: 'making_impact', label: 'Making a Difference', emoji: '🌟' },
    { value: 'remote_work', label: 'Remote/Flexible Work', emoji: '🏠' },
    { value: 'job_security', label: 'Job Security & Stability', emoji: '🛡️' },
    { value: 'innovation', label: 'Innovation & Cutting-Edge', emoji: '🚀' },
    { value: 'work_life_balance', label: 'Work-Life Balance', emoji: '⚖️' },
    { value: 'entrepreneurship', label: 'Entrepreneurship Opportunities', emoji: '💼' },
    { value: 'self_expression', label: 'Creative Self-Expression', emoji: '🎭' }
  ];

  const learningStyleOptions = [
    { value: 'visual', label: 'Visual Learner', description: 'I learn best through images, diagrams, and demonstrations' },
    { value: 'hands-on', label: 'Hands-On Learner', description: 'I learn best by doing and practicing' },
    { value: 'reading', label: 'Reading/Writing Learner', description: 'I learn best through reading and taking notes' },
    { value: 'discussion', label: 'Discussion-Based Learner', description: 'I learn best through conversations and debates' }
  ];

  const toggleInterest = (interest: string) => {
    setSelectedInterests(prev =>
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  const togglePriority = (priority: string) => {
    setSelectedPriorities(prev =>
      prev.includes(priority)
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  const handleGetMatches = async () => {
    setIsLoading(true);
    try {
      const results = await calculateMatches({
        interests: selectedInterests,
        work_style: workStyle,
        priorities: selectedPriorities,
        learning_style: learningStyle
      });
      setMatches(results);
      setStep(5);
    } catch (error) {
      console.error('Error calculating matches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetQuiz = () => {
    setStep(1);
    setMatches([]);
    setSelectedInterests([]);
    setWorkStyle('');
    setSelectedPriorities([]);
    setLearningStyle('');
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Find Your Perfect Career Match</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Answer a few questions to discover careers that align with your interests, work style, and priorities
            </p>
          </div>

          {/* Progress Bar */}
          {step < 5 && (
            <div className="max-w-2xl mx-auto mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Step {step} of 4</span>
                <span className="text-sm text-muted-foreground">{Math.round((step / 4) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(step / 4) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Step 1: Interests */}
          {step === 1 && (
            <div className="max-w-3xl mx-auto">
              <div className="bg-card p-8 rounded-lg shadow-sm">
                <h2 className="text-2xl font-bold mb-2">What are you interested in?</h2>
                <p className="text-muted-foreground mb-6">Select all that apply (at least 2)</p>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                  {interestOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => toggleInterest(option.value)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedInterests.includes(option.value)
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-2xl mb-1">{option.emoji}</div>
                      <div className="font-medium text-sm">{option.label}</div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setStep(2)}
                  disabled={selectedInterests.length < 2}
                  className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Work Style */}
          {step === 2 && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-card p-8 rounded-lg shadow-sm">
                <h2 className="text-2xl font-bold mb-2">What's your work style?</h2>
                <p className="text-muted-foreground mb-6">Choose the one that best describes you</p>

                <div className="space-y-3 mb-8">
                  {workStyleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setWorkStyle(option.value)}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                        workStyle === option.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="font-medium mb-1">{option.label}</div>
                      <div className="text-sm text-muted-foreground">{option.description}</div>
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 bg-muted text-foreground py-3 rounded-lg font-medium hover:bg-muted/80"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!workStyle}
                    className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Priorities */}
          {step === 3 && (
            <div className="max-w-3xl mx-auto">
              <div className="bg-card p-8 rounded-lg shadow-sm">
                <h2 className="text-2xl font-bold mb-2">What matters most to you?</h2>
                <p className="text-muted-foreground mb-6">Select your top priorities (at least 2)</p>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                  {priorityOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => togglePriority(option.value)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedPriorities.includes(option.value)
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-2xl mb-1">{option.emoji}</div>
                      <div className="font-medium text-sm">{option.label}</div>
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 bg-muted text-foreground py-3 rounded-lg font-medium hover:bg-muted/80"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    disabled={selectedPriorities.length < 2}
                    className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Learning Style */}
          {step === 4 && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-card p-8 rounded-lg shadow-sm">
                <h2 className="text-2xl font-bold mb-2">How do you learn best?</h2>
                <p className="text-muted-foreground mb-6">Choose your preferred learning style</p>

                <div className="space-y-3 mb-8">
                  {learningStyleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setLearningStyle(option.value)}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                        learningStyle === option.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="font-medium mb-1">{option.label}</div>
                      <div className="text-sm text-muted-foreground">{option.description}</div>
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(3)}
                    className="flex-1 bg-muted text-foreground py-3 rounded-lg font-medium hover:bg-muted/80"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleGetMatches}
                    disabled={!learningStyle || isLoading}
                    className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Finding Matches...' : 'Get My Matches'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Results */}
          {step === 5 && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-card p-8 rounded-lg shadow-sm mb-6">
                <h2 className="text-3xl font-bold mb-2">Your Career Matches</h2>
                <p className="text-muted-foreground mb-6">
                  Based on your responses, here are careers that align with your profile
                </p>

                <div className="space-y-4">
                  {matches.slice(0, 6).map((match, index) => (
                    <Link
                      key={match.domain.id}
                      to={`/explore/careers/${match.domain.slug}`}
                      className="block p-6 border-2 border-border rounded-lg hover:border-primary transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{match.domain.icon_emoji}</span>
                          <div>
                            <h3 className="text-xl font-bold">{match.domain.name}</h3>
                            <p className="text-muted-foreground">{match.domain.short_description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-primary">{match.match_score}%</div>
                          <div className="text-sm text-muted-foreground">Match</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span>💰 ${(match.domain.avg_salary_usd / 1000).toFixed(0)}k avg salary</span>
                        <span>📈 {match.domain.job_growth_percent}% growth</span>
                        <span>📚 {match.domain.specialization_count} specializations</span>
                      </div>

                      {match.matched_reasons.length > 0 && (
                        <div className="bg-muted p-3 rounded">
                          <div className="text-sm font-medium mb-1">Why this matches:</div>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {match.matched_reasons.slice(0, 3).map((reason, idx) => (
                              <li key={idx}>✓ {reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>

                <div className="mt-8 flex gap-3">
                  <button
                    onClick={resetQuiz}
                    className="flex-1 bg-muted text-foreground py-3 rounded-lg font-medium hover:bg-muted/80"
                  >
                    Retake Quiz
                  </button>
                  <Link
                    to="/explore/careers"
                    className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 text-center"
                  >
                    Explore All Careers
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CareerMatchPage;
